export const gettingStarted = `# Getting Started

Welcome to the Wealth Financial Systems — your all-in-one platform for market monitoring, trade research, and financial planning. This guide walks you through setting up your account and the recommended path to get the most out of every tool.

## Account Setup

1. **Register** — Create your account with an email and password. You'll receive a 6-digit verification code.
2. **Verify Email** — Enter the code sent to your inbox (check spam if needed). This unlocks all features.
3. **Choose Alerts** — During setup you can enable Hourly IV Alerts and the Daily Report. You can change these anytime in Account Settings.

## Your Recommended Journey

Follow these steps in order to build a complete financial picture and get the most from the platform.

### Step 1 — Set Up Your Watchlist

Before any monitoring or research can work, you need to tell the system which symbols to track.

- Go to **IV Reports** in the sidebar and click **List Preferences**
- Add individual stock tickers or select from pre-built lists
- Your watchlist is shared across IV monitoring, Options Research, and Stock Research — set it up once and everything uses it

### Step 2 — Configure Your Notifications

Decide how you want to be alerted to market activity.

- Go to **Account Settings** (or revisit the Subscription Setup page)
- Enable **Hourly IV Alerts** to receive real-time updates during market hours when significant volatility changes are detected
- Enable the **Daily Report** for a comprehensive end-of-day summary with AI-powered analysis
- If you're a Premier subscriber, you can also enable the **Stock Daily Report** from its page

### Step 3 — Know Your Numbers

Use the free financial planning tools to understand where you stand today.

- Open the **Net Worth Tracker** and enter your accounts — savings, investments, real estate, and liabilities
- Run the **FIRE Strategy Calculator** to model your path to financial independence
- Check the **Lifecycle Planner** for age-based allocation recommendations
- Use the **Bucket Visualizer** to create a personalized investment allocation plan

### Step 4 — Monitor the Markets

Once your watchlist is set, the platform works for you automatically.

- **IV Reports** will alert you when implied volatility spikes or drops on your symbols
- **Options Research** generates trade recommendations when high-probability setups are detected (Premium)
- **Stock Research** provides technically-scored stock trade ideas with trailing stops (Premium)
- **Stock Daily Report** delivers a downloadable pre-market report each trading day (Premium)
- **Index Options Research** delivers ranked SPY put-selling candidates with AI analysis (Pro) — requires connecting your TastyTrade account in Account Settings

### Step 5 — Review and Learn

- Open the **Playbook** for an investing guide you can read or listen to as audio
- Check back to this **Help Docs** section anytime for guidance on specific tools
- Use **Contact Support** to submit a ticket if you need help

## Quick Tips

- Most features require email verification before access.
- Premier features are clearly marked — you'll see an upgrade prompt if you try to access them on the Free plan.
- The sidebar collapses by clicking the arrow button at the top for more screen space. On mobile, tap the hamburger menu to open it.
`;

export const dashboard = `# Dashboard

The Dashboard is your home base when you log in. It provides an at-a-glance view of your system and quick access to every tool.

## How to Use

When you first log in, the Dashboard shows you exactly what to do next.

1. **Check the Start Here block** — If you're new, this walks you through initial setup: verifying your email, configuring notifications, and building your watchlist.
2. **Review the Verification Alert** — If your email isn't verified yet, click through the alert at the top to complete verification so you can access all features.
3. **Scan the Status Cards** — These summarize your active services at a glance:
   - **Subscription Status** — Free or Premier plan
   - **Email Notifications** — Whether hourly alerts and daily reports are on
   - **Active Research** — Count of open options and stock research picks (Premium)
   - **Watchlist** — Number of symbols being monitored
4. **Use the Quick Links** — Below the status cards, direct links take you to the most commonly used tools so you don't need to navigate the sidebar.

Each status card links directly to its management page — click any card to jump into configuration.
`;

export const optionsSignals = `# Options Research

Options Research is a Premier feature that provides algorithmically generated trade recommendations based on implied volatility analysis.

## How to Use

1. Navigate to **Options Research** in the Market Monitoring section of the sidebar.
2. Review the **Active Notches** tab first — these are currently open positions being monitored.
3. Switch to the **New Notches** tab to see recently generated readings you haven't acted upon yet.
4. Click into any reading to see the full detail: strategy, strike prices, expiration, and risk level.
5. Check the **Notch History** tab to review closed and expired readings with their outcomes.

## How Readings Work

The system analyzes options chains across your watchlist symbols hourly during market hours. When a high-probability setup is detected — typically involving elevated IV rank and favorable risk/reward — a reading is generated.

## Reading Components

Each reading includes:

- **Symbol & Direction** — The underlying ticker and whether the reading is bullish, bearish, or neutral
- **Strategy** — The recommended options strategy (e.g., Iron Condor, Bull Put Spread, Covered Call)
- **IVR Score** — Implied Volatility Rank, showing where current IV sits relative to its 52-week range (0–100%)
- **Reading Score** — An overall confidence score combining IV analysis, technicals, and market conditions
- **Entry/Exit Targets** — Suggested strike prices and expiration dates
- **Risk Level** — Conservative, Moderate, or Aggressive classification

## Important Notes

- Readings are generated hourly during market hours (9 AM – 4 PM ET)
- Your watchlist must be set up in IV Reports > List Preferences before readings can be generated
- Past performance does not guarantee future results
- Always perform your own due diligence before entering any trade
`;

export const stockSignals = `# Stock Research

Stock Research is a Premier feature providing technically-driven stock trade recommendations. Unlike Options Research which focuses on implied volatility, Stock Research uses a multi-factor technical scoring system.

## How to Use

1. Navigate to **Stock Research** in the Market Monitoring section of the sidebar.
2. The main view shows **Active Notches** — open positions currently being monitored with trailing stops.
3. Click any reading to expand the full detail view including score breakdown, fundamentals, sentiment, and rationale.
4. Watch the **Trailing Stop** level on active readings — it automatically adjusts as the position moves in your favor.
5. Check **Notch History** to review past readings and their outcomes.

## Technical Scoring System

Each stock reading is scored on a 0–100 scale based on multiple indicators:

- **RSI (14-day)** — Relative Strength Index for overbought/oversold conditions
- **MACD Histogram** — Momentum and trend direction
- **SMA 20/50** — Short and medium-term moving average positioning
- **Bollinger Band Position** — Where price sits within the bands (shown as %)
- **Volume Ratio** — Current volume relative to average, confirming moves
- **ATR (14-day)** — Average True Range for volatility context

## Score Breakdown

Each reading provides a detailed breakdown showing how many points each technical indicator contributed. This helps you understand exactly why a reading was generated and which factors are strongest.

## Fundamentals & Sentiment

Readings also include:

- **Fundamentals Summary** — Key ratios like P/E, debt/equity, and ROE
- **Sentiment Score** — Market sentiment analysis shown as a percentage
- **Rationale** — A written explanation of the reading thesis

## Reading Lifecycle

Readings move through stages: Generated → Active (if entered) → Closed (hit target, stopped out, or expired). Trailing stops automatically adjust based on ATR to account for each stock's volatility.
`;

export const howWePickTrades = `# How We Pick Stock Trades

## Overview

Our system scans over 100 high-liquidity stocks every hour during market hours, looking for swing trade setups in both directions — long (buy) and short (sell). Each candidate goes through a multi-layer scoring process before a reading is generated. Only the strongest setups make it through.

---

## The Selection Process

### Step 1: Technical Analysis

Every stock is evaluated on multiple technical indicators simultaneously. We look for **confluence** — multiple signals agreeing at once, not just one indicator flashing green.

What we analyze:
- **Trend strength** — Is the stock in a clear, established trend or choppy and directionless?
- **Momentum** — Is buying or selling pressure accelerating or fading?
- **Volume** — Are institutional players participating, or is it thin retail volume?
- **Volatility positioning** — Where is price relative to its recent range? Are we catching a move early or late?
- **Institutional flow** — Is price trading above or below where large players are positioned?

Each factor contributes to an overall technical score. A stock must score well across **multiple** dimensions — a single strong indicator is not enough.

### Step 2: Market Sentiment

We aggregate sentiment data from multiple financial news and analysis sources to gauge overall market mood for each stock:

- **Bullish / Bearish classification** — What's the consensus view?
- **Confidence level** — How strong is the consensus?
- **Key catalysts** — Earnings, analyst upgrades/downgrades, sector news

Sentiment acts as a **filter and modifier**:
- A long reading with bearish sentiment gets penalized
- A short reading with bullish sentiment is **blocked entirely** — we don't fight positive consensus
- Strong sentiment alignment boosts the reading's score

### Step 3: Market Direction Check

Before generating any reading, we check the broader market environment:

- **S&P 500 trend** — Is the overall market supportive of the trade direction?
- **Market momentum** — Is the broad market overbought, oversold, or neutral?
- **Sector strength** — Is the stock's sector ETF confirming or contradicting the reading?
- **Volume trend** — Is the sector seeing accumulation (buying) or distribution (selling)?

A stock may have perfect technicals, but if the broad market and its sector are moving against the trade direction, the reading is downgraded or blocked.

### Step 4: Scoring & Threshold

All factors combine into a final score (0–100):

| Component | What It Measures |
|-----------|-----------------|
| Technical Score | Price action, momentum, trend, volume, volatility |
| Sentiment Adjustment | News and analyst consensus (+/- modifier) |
| Market Direction | Broad market and sector alignment (+/- modifier) |

**Only readings scoring above our minimum threshold are published.** Most stocks are evaluated and rejected every hour — typically fewer than 5% pass on any given scan.

---

## Long vs. Short Readings

### Long (Buy) Readings
We look for stocks with:
- Confirmed uptrends with healthy momentum
- Oversold conditions with reversal setups
- Breakouts above key resistance with volume confirmation

### Short (Sell) Readings
We look for stocks with:
- Confirmed downtrends with accelerating selling pressure
- Overbought conditions showing signs of reversal
- Breakdowns below key support levels

**Safety guardrails for shorts:**
- Never short a stock with bullish market sentiment
- Sector must not be showing strength (we check the sector ETF)
- Higher score threshold required than long readings
- Broader market must not be in a strong uptrend

---

## After a Reading Is Generated

### Entry, Stop & Target
Every reading includes three price levels:

- **Entry** — The recommended price to enter the trade
- **Stop Loss** — The price where we're wrong; exit to limit losses
- **Target** — The expected profit target based on recent price structure

These levels are calculated from actual price data — support/resistance zones, volatility range, and risk/reward ratios.

### Active Monitoring
Once a reading is open, the system monitors it every hour:

- **Trailing stop** — As the trade moves in your favor, the stop adjusts upward (for longs) or downward (for shorts) to lock in gains
- **Exit signals** — If the original conditions that triggered the trade reverse, we flag it
- **P&L tracking** — Real-time unrealized profit/loss displayed on the dashboard
- **Hold time** — We enforce a minimum hold period to avoid whipsaws from short-term noise

### Automatic Closing
A reading is closed when:
- Price hits the stop loss → **Loss limited**
- Price hits the target → **Profit captured**
- Exit conditions trigger → **Conditions changed, take profit or cut losses**
- Maximum hold time reached → **Re-evaluate, don't hold indefinitely**

---

## What Makes Our Approach Different

1. **Multi-factor confluence** — We require agreement across technicals, sentiment, and market direction. One green light isn't enough.

2. **Directional awareness** — We trade both sides of the market. When conditions favor selling, we generate short readings instead of sitting idle.

3. **Sentiment as a safety net** — We never fight strong market consensus. If the market is bullish on a stock, we won't short it regardless of what the chart says.

4. **Sector validation** — We check if the stock's entire sector supports the trade. A bearish stock in a bullish sector is a red flag.

5. **Continuous monitoring** — Readings aren't fire-and-forget. Every open position is re-evaluated hourly with dynamic stops and exit logic.

6. **Risk-first design** — Every reading has a defined stop loss. Position monitoring includes trailing stops and reversal detection to protect capital.

---

## Reading Status Definitions

| Status | Meaning |
|--------|---------|
| **OPEN** | Active reading — trade is live and being monitored |
| **CLOSED** | Trade completed — hit target, stop, or exit condition |
| **EXPIRED** | Reading was not acted on within the entry window |

---

## Frequency & Coverage

- **Scan frequency**: Every hour during market hours (9:30 AM – 4:00 PM ET)
- **Universe**: 100+ high-liquidity US stocks across all major sectors
- **Directions**: Long and Short
- **Typical output**: 2–8 new readings per day (varies with market conditions)
- **Pre-market report**: Daily email summary at 8:00 AM ET

---

*This document describes our research methodology at a high level. Specific indicator thresholds, scoring weights, and proprietary algorithms are not disclosed.*
`;

export const ivReports = `# IV Reports

IV Reports provide automated implied volatility monitoring for the symbols on your watchlist. This is the core monitoring engine of the platform.

## How to Use

1. Navigate to **IV Reports** in the Market Monitoring section of the sidebar.
2. **Set up your watchlist first** — Click the **List Preferences** link to add symbols. This is a critical first step because your watchlist drives all monitoring and research across the platform.
3. In List Preferences, you can:
   - Type individual stock tickers to add them
   - Select from pre-built symbol lists for quick setup
   - Validate symbols against the available database
   - Remove symbols you no longer want to track
4. Back on the IV Reports page, review the **How IV Monitoring Works** section for context on what the system tracks.
5. Enable **Hourly IV Alerts** and/or the **Daily Report** in your Account Settings to receive email notifications.

## How IV Monitoring Works

The system checks implied volatility levels for your watchlist symbols on a regular schedule. When notable changes occur — such as a spike in IV rank or a significant shift in the volatility surface — you receive an alert.

## Email Alerts

There are two types of notifications:

- **Hourly IV Alerts** — Sent every hour during market hours when meaningful IV changes are detected
- **Daily Report** — A comprehensive end-of-day summary of IV activity across all your watched symbols

You can enable or disable each independently in Account Settings or on the IV Reports page.

## Understanding the Reports

Reports include:

- **Current IVR** — Where implied volatility ranks within its annual range
- **IV Change** — How much IV has moved since the last report
- **Notable Events** — Earnings dates, ex-dividend dates, or other catalysts affecting IV
- **Historical Context** — Comparison to recent IV levels for trend identification
`;

export const netWorthTracker = `# Net Worth Tracker

The Net Worth Tracker helps you monitor and visualize your complete financial picture over time. This is a free tool available to all users.

## How to Use

1. Navigate to **Net Worth Tracker** in the Wealth Overview section of the sidebar.
2. Click **Add Account** to create your first entry.
3. For each account, select the type, enter a name, and input the current value:
   - **Cash & Savings** — Bank accounts, emergency funds, high-yield savings
   - **Investment Accounts** — Brokerage, IRA, 401(k), HSA
   - **Real Estate** — Property values minus outstanding mortgage balances
   - **Other Assets** — Vehicles, collectibles, crypto, business equity
   - **Liabilities** — Credit cards, student loans, car loans, other debts
4. Add all your accounts to build a complete picture — the more complete your entries, the more accurate your net worth calculation.
5. **Update values periodically** (monthly or quarterly is a good cadence) to build a history and track your progression over time.

## Progression Chart

Once you have entries over multiple time periods, the main visualization shows your total net worth as a time-series chart broken down by asset category.

Key chart features:

- **Stacked area view** — See how each category contributes to total net worth
- **Trend line** — Overall trajectory with smoothing
- **Date range selector** — Focus on specific time periods

## Growth Rate

The tracker calculates your net worth growth rate:

- Month-over-month change (amount and percentage)
- Year-over-year change
- Annualized growth rate since first entry

This helps you set benchmarks and understand whether your wealth-building pace is accelerating or decelerating.
`;

export const fireCalculator = `# FIRE Strategy Calculator

The FIRE (Financial Independence, Retire Early) Strategy Calculator helps you model different paths to financial independence. This is a free tool available to all users.

## How to Use

1. Navigate to **FIRE Strategy Calculator** in the Capital Allocation section of the sidebar.
2. Enter your financial snapshot:
   - **Current Age** — Your age today
   - **Target Retirement Age** — When you want to achieve financial independence
   - **Current Savings** — Total investable assets today
   - **Annual Income** — Your gross or net annual income
   - **Annual Expenses** — Your yearly spending
   - **Savings Rate** — Percentage of income saved and invested
   - **Expected Return** — Anticipated average annual investment return
3. Click **Calculate** to generate projections across multiple FIRE strategies.
4. Compare the strategies side by side to see which approach fits your lifestyle and goals.
5. Review the Monte Carlo simulation results to understand the probability of success under varying market conditions.

## Strategy Comparison

The calculator models multiple FIRE strategies simultaneously:

- **Traditional FIRE** — Standard 25x annual expenses target (4% withdrawal rule)
- **Lean FIRE** — Minimalist approach with reduced expenses
- **Fat FIRE** — Higher target for a more comfortable retirement
- **Barista FIRE** — Partial retirement with part-time income supplementing investments
- **Coast FIRE** — Save aggressively early, then let compounding do the work

Each strategy shows the projected timeline, required savings amount, and monthly contribution needed.

## Monte Carlo Simulation

The calculator stress-tests your plan against market variability by running thousands of scenarios with randomized returns based on historical distributions.

Results show:

- **Success probability** — Percentage of simulations where your money lasts through retirement
- **Confidence ranges** — 10th, 50th, and 90th percentile outcomes
- **Recommended adjustments** — Suggestions if your success probability is below 90%
`;

export const lifecyclePlanner = `# Lifecycle Planner

The Lifecycle Planner provides age-based asset allocation guidance following established lifecycle investing principles. This is a free tool available to all users.

## How to Use

1. Navigate to **Lifecycle Planner** in the Wealth Overview section of the sidebar.
2. Enter your **current age** and **risk tolerance** level.
3. The planner automatically generates a recommended portfolio allocation tailored to your life stage.
4. Review the allocation breakdown — it shows the percentage split between equities, bonds, and other asset classes.
5. Use the guidance to inform how you rebalance your actual portfolio. The recommendations update as your age changes, following a glide path similar to target-date funds but with more customization.

## Lifecycle Stages

The planner defines four distinct financial life stages:

1. **Accumulation** (20s–30s) — Early career. Higher equity allocation for maximum growth with small fixed-income positions. Focus on maximizing your savings rate.
2. **Growth** (40s–50s) — Mid-career. Gradually increasing bond allocation to reduce volatility while still growing wealth.
3. **Preservation** (55+) — Pre-retirement. Emphasis on capital preservation and income generation. Protecting what you've built.
4. **Distribution** — Retirement. Generating sustainable income from your investment portfolio.

Each stage comes with specific guidance on asset allocation percentages, contribution priorities, and risk management strategies.

## Bucket Approach

The planner integrates with the Bucket Visualizer to implement a bucket strategy:

- **Bucket 1 (Near-term)** — 1–2 years of expenses in cash and short-term bonds
- **Bucket 2 (Medium-term)** — 3–7 years in balanced funds and intermediate bonds
- **Bucket 3 (Long-term)** — Remaining assets in growth-oriented investments

This approach provides peace of mind during market downturns because your near-term spending needs are insulated from equity volatility.
`;

export const bucketVisualizer = `# Bucket Visualizer

The Bucket Visualizer is an interactive tool that creates a personalized investment allocation plan based on your life stage and goals.

## How to Use

1. Navigate to **Bucket Visualizer** in the Capital Allocation section of the sidebar.
2. The tool walks you through a 4-step wizard:

**Step 1 — Enter Your Monthly Investment Amount**
Type in how much you invest (or plan to invest) each month. This becomes the basis for your allocation.

**Step 2 — Select Your Life Stage**
Choose the stage that best describes where you are financially:
- **Starting Out** — Early in your career, building the foundation
- **Building Wealth** — Mid-career, growing and compounding
- **Approaching Freedom** — Nearing financial independence

**Step 3 — Answer Context Questions**
Answer three quick questions about your situation (emergency fund status, goals, and risk tolerance). These tailor the allocation to your specific needs.

**Step 4 — Review Your Results**
The tool generates a personalized plan including:
- An AI-written summary of your recommended strategy
- Four allocation buckets with monthly dollar amounts and percentages
- Key insights and a crash protection strategy
- Projected values over time including estimated monthly dividends
- A 30-year Wealth Flywheel projection showing compounding growth

3. Click **Download PDF** to save your personalized allocation plan.
4. Use the links at the bottom to explore the **FIRE Calculator** or **Playbook** for deeper planning.
`;

export const playbook = `# Playbook

The Playbook is your investing reference guide — a curated document covering foundational investing principles, strategies, and practical guidance.

## How to Use

1. Navigate to **Playbook** in the Wealth Overview section of the sidebar.
2. Read the overview section on the page for a summary of what the Playbook covers.
3. Choose how you want to consume the content:
   - **Listen to Summary** — A quick audio overview hitting the key points
   - **Listen to Podcast** — The full Playbook content in audio format, perfect for commuting or working out
   - **Listen to Debate** — An audio discussion exploring different perspectives on investing approaches
   - **Open Playbook (PDF)** — Download or view the complete document as a PDF
4. The Playbook is designed to complement the platform's tools. Read it to understand the "why" behind the allocation strategies you see in the Lifecycle Planner and Bucket Visualizer.

## What It Covers

The Playbook covers topics including:

- Core investing principles and long-term wealth building
- How to think about risk and asset allocation at different life stages
- The case for boring, consistent investing vs. active trading
- How to use the platform's tools together as part of a unified strategy
`;

export const subscriptionsBilling = `# Subscriptions & Billing

The Wealth Financial Systems offers free tools and individual research products you can subscribe to separately.

## Free Tier

All users get access to these tools at no cost:

- **Dashboard** — System status and quick links
- **Net Worth Tracker** — Monitor your finances over time
- **FIRE Strategy Calculator** — Model your path to financial independence
- **Lifecycle Planner** — Age-based allocation guidance
- **Bucket Visualizer** — Personalized investment allocation plans
- **Playbook** — Investing reference guide with audio content
- **Help Docs** and **Contact Support**

## Research Products & Pricing

Each research product is a separate subscription. Subscribe only to what you trade.

| Product | Price | What You Get |
|---------|-------|--------------|
| **Stock & Options Research** | $29.99 for first 2 months, then $29.99/mo | Stock swing readings, daily reports, IV monitoring, custom watchlists, stock scanner |
| **SPX Notches** | $79/mo | SPX 0DTE and multi-day credit spread readings with AI analysis |
| **SPY Notches** | $49/mo | SPY options put-sell and credit spread readings |
| **ES Notches** | $79/mo | E-mini S&P 500 futures options readings |
| **MES Notches** | $49/mo | Micro E-mini S&P 500 futures options readings |
| **All Notches Bundle** | $199/mo | SPX + SPY + ES + MES — save $57/mo vs individual |
| **Algo Trading Engine** | $49.99/mo per engine | Automated stock trading with AI-driven strategies (requires Stock & Options subscription) |

## How to Subscribe

1. Navigate to the **Upgrade** page from the navigation menu.
2. Choose one or more products and complete payment through PayPal.
3. Access activates upon payment confirmation (usually within minutes).
4. You can add more products at any time — each is a separate subscription.

## 60-Day Money-Back Guarantee

Every subscription includes our confidence guarantee:

- **Days 1-7**: Minimum commitment period. Explore the platform and research.
- **Days 7-30**: Full refund available. Cancel from your Profile page and receive a complete refund within 3-5 business days.
- **Days 30-60**: Cancel anytime. No refund, but you keep full access until the 60-day period ends. No further charges.
- **After 60 days**: Standard monthly billing. Cancel anytime and keep access through the end of your billing period.

## Cancellation Policy

You can cancel any product subscription at any time:

1. **From your Profile page**: Go to Profile and click "Cancel" next to the subscription you want to cancel.
2. **From PayPal**: Use the subscription management section in your PayPal account.
3. **Contact support**: Email support@wealthfinancialsystems.com for assistance.

When you cancel:
- Access continues until the end of your current billing period.
- No future charges will be made.
- You can re-subscribe at any time.
- Cancelling one product does not affect other active subscriptions.

**Important**: Cancelling the All Notches Bundle disables SPX, SPY, ES, and MES access — unless you also have individual subscriptions for those products.

## Refund Policy

- Refunds are available **between days 7 and 30** of any subscription.
- Refund requests are processed immediately via PayPal.
- Refunds typically appear in 3-5 business days.
- Each product subscription has its own independent refund window.
- After the 30-day refund window, no refunds are issued, but you retain access through your prepaid period.
- Refunds are one-time per subscription. Re-subscribing starts a new refund window.

## Billing Details

- All billing is handled through **PayPal** — we never store your payment information.
- Each product is billed independently on its own monthly cycle.
- Subscriptions renew automatically until cancelled.
- Introductory pricing (Stock & Options, Algo) transitions to regular pricing after the intro period (6 months or 1 year respectively).

## Why Our Research — Not ChatGPT

Our research system is fundamentally different from ChatGPT or other AI chatbots:

- **Live market data**: We analyze real bid/ask spreads, IV surfaces, and option chains from TastyTrade — not cached or guessed data.
- **Quantitative models**: Backtested weighting algorithms evaluate hundreds of candidates per snapshot, not a language model generating text.
- **Risk management**: Symbol-specific minimum buffers, credit thresholds, and position sizing built into every reading.
- **Transparency**: Every reading includes a downloadable Word report with complete rationale, score breakdown, and market context.

ChatGPT cannot see live prices, evaluate real option chains, or manage trading risk. Our system does all of this automatically, every hour.

## Need Help?

If you have billing questions or issues with your subscription, submit a support ticket under the "Billing" category via **Contact Support**, or email **support@wealthfinancialsystems.com**.
`;

export const signalSchedule = `# Notches Generation Schedule

Our research system collects live option chain data and generates AI-validated recommendations on a fixed schedule throughout the trading day.

## Pre-Market (Before Market Open)

Readings are generated before the market opens so you can plan your trades ahead of time.

| Product | Time (ET) | What Happens |
|---------|-----------|-------------|
| **SPX** | 8:30 AM | Live SPX option chain collected, scored, and analyzed by two independent AIs |
| **SPY** | 8:45 AM | Live SPY option chain collected, scored, and analyzed |
| **ES / MES** | 9:00 AM | Live E-mini and Micro E-mini futures option chains collected, scored, and analyzed |

## Market Hours

During the trading day, readings are refreshed multiple times with updated market data.

| Product | Times (ET) |
|---------|-----------|
| **SPX** | 9:45 AM, 12:45 PM, 3:45 PM |
| **SPY** | 10:20 AM, 1:20 PM, 3:20 PM |
| **ES / MES** | 10:40 AM, 1:40 PM, 4:40 PM |

## After-Hours

ES and MES futures trade nearly 24 hours. We capture after-hours snapshots for extended analysis.

| Product | Times (ET) |
|---------|-----------|
| **SPX** | 6:00 PM, 8:00 PM, 10:00 PM |
| **ES / MES** | 6:00 PM, 8:00 PM, 10:00 PM |

SPY does not have after-hours readings (equity options do not trade after hours).

## What Each Snapshot Includes

Every snapshot runs through our full pipeline:

1. **Live Data** — Real-time option chain data from TastyTrade (bid, ask, Greeks, IV)
2. **Quantitative Scoring** — Our model scores and ranks every candidate by time horizon
3. **Claude AI Analysis** — Independent analysis from Anthropic's Claude
4. **ChatGPT Analysis** — Independent analysis from OpenAI's ChatGPT
5. **Triple Validation** — Confidence level based on agreement across all three sources
6. **Report Generation** — Downloadable Word document with full analysis

## How to View Notches

- **SPX**: Navigate to SPX > Notches
- **SPY**: Navigate to Index (SPY) > Notches
- **ES / MES**: Navigate to Futures (ES/MES) > Notches
- **AI Analysis**: Click "AI Analysis" in the navigation for the detailed AI reasoning
- **Market Pulse**: Click the green "Market Pulse" button for live market conditions (RSI, sentiment, SMAs)

Use the snapshot selector on the Notches page to switch between different times of day.

## Important Notes

- All times are Eastern Time (ET) and follow daylight saving time
- Pre-market data uses option chains from the pre-market session — spreads may be wider than during regular hours
- After 3:30 PM ET, same-day (0DTE) options are automatically filtered out due to unreliable pricing near close
- Readings are for educational and analytical purposes only — always perform your own due diligence
`;

export const stockDailyReport = `# Stock Daily Report

The Stock Daily Report is a Premier feature that delivers a downloadable pre-market stock analysis report each trading day.

## How to Use

1. Navigate to **Stock Daily Report** in the Market Monitoring section of the sidebar.
2. The **This Week** tab shows reports from the current week. Each report card displays:
   - The report date (with a "TODAY" badge for the current day's report)
   - The SPY trend direction (bullish, bearish, or neutral)
   - Count of open positions and new readings
   - A **Download Report** button to get the full Word document
3. Click **Download Report** to save the report — it includes detailed charts, analysis, and research pick summaries.
4. Switch to the **History** tab to browse past reports. Use the time range buttons (4, 8, or 12 weeks) to control how far back you look.
5. Enable the **daily email** toggle at the top of the page to have reports delivered to your inbox automatically each morning before market open.
6. Use the **Refresh** button to check for the latest report if the market has just opened.

## What's in the Report

Each daily report is a Word document containing:

- Market overview and SPY trend analysis
- Summary of all open Stock Research positions
- New readings generated with entry details
- Technical indicator charts and analysis
- Fundamentals snapshot for featured stocks
`;

export const algoTrading = `# Algo Trading Engine

The Algo Trading Engine is a Premier feature that lets you build automated trading strategies powered by technical analysis, fundamentals, sentiment data, market conditions, and optional AI review. It connects to your Alpaca brokerage account and can scan the market in both long and short directions, generate readings, and optionally execute trades on your behalf.

## Prerequisites

- **Premier subscription** with Algo Trading enabled (contact support or admin)
- **Alpaca brokerage account** — either Paper (practice) or Live
- Your Alpaca **API Key** and **Secret Key** (generated from your Alpaca dashboard)

## Getting Started

### Step 1 — Create Your First Engine

1. Navigate to **Algo Engines** in the Trade section of the sidebar.
2. Click **Create Engine**.
3. Fill out the setup wizard:

**Engine Settings**
- **Engine Name** — A label for this engine (e.g., "My Momentum Scanner")
- **Scan Frequency** — How often the engine runs: every 15 min, 30 min, or 60 min (during market hours only, Mon–Fri)
- **Alpaca Environment** — Choose **Paper** to practice with virtual money, or **Live** for real trading
- **Alpaca API Key & Secret** — Your Alpaca credentials (stored securely in AWS Secrets Manager, never visible after creation)

**Policy Configuration**
- **Policy Name** — A label for this strategy rule set
- **Strategy** — Choose from 10 built-in strategies, Auto Directional, or build a Custom Strategy (see below)
- **Direction** — Auto (system picks best direction per stock), Long Only, or Short Only
- **Symbols** — Enter 1–5 stock tickers to scan (e.g., AAPL, MSFT, NVDA)
- **Risk Appetite** — Conservative, Moderate, Aggressive, Very Aggressive, or Max Risk (controls position sizing and score thresholds)

**Risk Controls**
- **Cash Reserve %** — Percentage of your account equity to keep in cash (default: 50%)
- **Max Position Size %** — Maximum size of any single trade as % of equity (default: 10%)
- **Max Open Trades** — Maximum concurrent positions (default: 5)
- **Max Daily Trades** — Maximum new trades per day (default: 3)
- **Max Drawdown % (per trade)** — Force-close a trade if its loss exceeds this % of entry equity (default: 3%, range 1-10%)
- **Max Daily Drawdown %** — Auto-pause the engine if daily losses exceed this % of account equity (default: 5%, range 1-25%)
- **Max Weekly Drawdown %** — Auto-pause the engine if weekly losses exceed this % of account equity (default: 10%, range 1-50%)
- **Scaled Entry (Tranches)** — Scale into positions gradually: tranche 1 enters at 40% size, tranche 2 adds 30% after price confirms (0.5x ATR move), tranche 3 adds final 30% (1.0x ATR move). Cancels remaining tranches if price moves 0.5x ATR against you. Choose 2 or 3 tranches. (default: off)
- **Trailing Stop** — Enable ATR-based trailing stops that ratchet up after consecutive gains (default: on)
- **Profit Protection** — Automatically close profitable trades when market reversal signals are detected (default: on)
- **Wash Sale Protection** — Block re-trading any symbol that was closed at a loss within the last 30 days (default: off). This helps you avoid IRS wash sale rule violations.

**Claude AI (Optional)**
- **Enable Claude AI** — Adds an AI analysis layer that reviews each reading before it's generated
- **Auto-Execute** — When enabled with Claude AI, trades that meet a high confidence threshold are executed automatically
- **Confidence Threshold** — Minimum score (25–100) required for auto-execution (default: 90). Lower thresholds trigger more trades but with less certainty.

4. Click **Create Engine** to save. The engine starts scanning immediately on the next scheduled interval.

### Step 2 — Monitor Readings

1. Go to **Algo Signals** to see readings your engine generates.
2. Each reading shows:
   - **Symbol**, **Direction** (LONG/SHORT badge), and **Action** (buy/sell)
   - **Strategy Score** — How well the stock matches the strategy conditions (0–100)
   - **Entry, Stop, and Target prices** — Calculated from current price and ATR volatility
   - **Sentiment** — Market sentiment from news and analyst data
   - **Indicators** — Technical values like RSI, MACD, Bollinger Bands, AVWAP
   - **Claude Analysis** (if enabled) — AI confidence score, recommendation, and rationale
3. For pending readings, click **Execute** to place the trade, or let them expire if you're not interested.

### Step 3 — Track Trades

1. Go to **Algo Trades** to see all executed trades.
2. Each trade shows entry price, current price, unrealized P&L, and status.
3. The engine automatically monitors open trades with multiple protection layers:
   - **Max Drawdown** — Force-closes if loss exceeds your configured % of entry equity
   - **Trailing Stop** — ATR-based stop that ratchets up after consecutive gains, locking in profits
   - **Profit Protection** — Closes profitable trades when market reversal signals are detected
   - **Stop Loss / Take Profit** — Standard bracket order legs or suggested levels
4. Closed trades show the **exit reason** (e.g., stop_loss, take_profit, trailing_stop, max_drawdown, profit_protection).

---

## Strategies

### Auto Directional
Evaluates all 8 directional strategies (4 long + 4 short) for each stock and picks the highest-scoring direction. The engine automatically trades long or short based on what the market is offering.

**Best for**: Traders who want the system to decide direction based on current conditions.

### Long Strategies (Buy)

**Momentum** — Catches stocks in strong uptrends with volume confirmation. RSI 55-72, volume 1.4x+, price above SMA20, positive MACD. *Best for riding existing trends.*

**Oversold Bounce** — Mean-reversion targeting extreme oversold conditions. RSI below 32, price at lower Bollinger Band, volume picking up. *Best for catching bounces after sharp selloffs.*

**Breakout** — Stocks breaking out of consolidation with strong volume. Price near 20-day high, volume 1.8x+, positive momentum. *Best for new highs and range breakouts.*

**Trend Following** — Rides confirmed uptrends using moving average alignment. SMA20 above SMA50, MACD positive, healthy RSI. *Best for longer-term trend rides.*

### Short Strategies (Sell Short)

**Bearish Momentum** — Catches stocks in accelerating downtrends. RSI 28-45, volume surging, price below SMA20, negative MACD. *Best for riding confirmed downtrends.*

**Overbought Reversal** — Targets overextended stocks showing reversal. RSI above 68, upper Bollinger Band, MACD declining. *Best for fading stretched rallies.*

**Breakdown** — Stocks breaking below key support with volume. Price at 20-day low, volume 1.8x+, negative momentum. *Best for catching breakdowns.*

**Bearish Trend Following** — Rides confirmed downtrends using death cross. SMA20 below SMA50, MACD negative. *Best for sustained bear trends.*

**Safety guardrails on all short readings**: Short readings are automatically blocked when sentiment is bullish or very bullish. Sector ETF strength and broad market direction are also checked.

### Custom Strategy
Build your own strategy from scratch using any combination of 22 indicators across 5 categories (see Condition Editor below). You can also load from 10 professional presets as a starting point and customize from there.

---

## Condition Editor

The condition editor lets you build or customize entry conditions using 22 indicators across 5 categories. For built-in strategies, your conditions are merged on top (overriding matching indicators). For custom strategies, your conditions ARE the strategy.

### Available Indicators

**Technical - Price & Momentum**
| Indicator | Description | Typical Range |
|-----------|-------------|---------------|
| RSI (14) | Relative Strength Index | 0-100 (below 30 oversold, above 70 overbought) |
| MACD Histogram | Momentum direction | Positive = bullish, negative = bearish |
| BB Position | Bollinger Band position | 0 = lower band, 1 = upper band |
| Volume Ratio | Current vs 20-day avg volume | 1.5 = 50% above average |
| ATR % | Average True Range as % of price | 1-5% typical |

**Technical - Trend**
| Indicator | Description | Key Levels |
|-----------|-------------|------------|
| Price vs SMA20 | Ratio to 20-day moving average | Above 1.0 = uptrend |
| SMA20 vs SMA50 | Golden/death cross ratio | Above 1.0 = golden cross |
| Price vs 20d High | Distance from 20-day high | 1.0 = at high |
| Price vs 20d Low | Distance from 20-day low | 1.0 = at low |
| AVWAP Distance % | Distance from institutional VWAP | Positive = above VWAP |

**Fundamental (from FMP)**
| Indicator | Description |
|-----------|-------------|
| P/E Ratio | Price to earnings (lower = cheaper) |
| Debt/Equity | Below 1 = more equity than debt |
| ROE % | Return on equity (higher = more efficient) |
| Current Ratio | Above 1 = can cover short-term debts |
| Dividend Yield % | Annual dividend / price |
| Market Cap ($B) | Company size in billions |
| 1-Month Price Change % | 30-day price performance |
| 3-Month Price Change % | 90-day price performance |

**Market Conditions**
| Indicator | Description |
|-----------|-------------|
| VIX Level | Market fear (below 20 calm, above 30 volatile) |
| SPY RSI | S&P 500 momentum (0-100) |
| SPY Trend | 1 = above SMA50 (bull), 0 = below (bear) |

**Sentiment**
| Indicator | Description |
|-----------|-------------|
| Sentiment Score | 0-1 aggregated news sentiment |

### Professional Presets

When building a custom strategy, you can load from 10 preset strategies based on well-known professional approaches:

- **CANSLIM Growth** — William O'Neil's growth stock method
- **Value + Momentum** — AQR/Cliff Asness style value with momentum
- **Mean Reversion** — Buy oversold quality stocks below VWAP
- **Trend Following (Turtle)** — Richard Dennis turtle trading method
- **Dividend Compounder** — Buffett-style profitable dividend payers
- **Volatility Breakout** — Minervini SEPA squeeze breakouts
- **Short Squeeze Setup** — Oversold + volume surge reversal plays
- **Bear Market Profiter** — Short broken trends in high-VIX markets
- **Large Cap Quality** — Institutional blue-chip filter
- **Calm Market Scalper** — Low-VIX mean reversion on minor pullbacks

Click any preset to load its conditions, then customize weights, thresholds, or add/remove conditions to fit your style.

---

## Risk Appetite Explained

| Setting | Min Score | Position Size | Stop Loss | Max Trades |
|---------|-----------|---------------|-----------|------------|
| Conservative | 75 | 2% of equity | Tight (2x ATR) | 3 |
| Moderate | 65 | 5% of equity | Medium (2.5x ATR) | 7 |
| Aggressive | 55 | 10% of equity | Wide (3x ATR) | 15 |
| Very Aggressive | 40 | 15% of equity | Wider (3.5x ATR) | 20 |
| Max Risk | 25 | 20% of equity | Widest (4x ATR) | 30 |

**Conservative** requires the strongest readings with small positions and tight stops. **Max Risk** accepts weak readings with large positions — use with extreme caution and only on paper trading until you understand the behavior. Start with **Moderate** if you're unsure.

---

## AI Analysis (Claude + ChatGPT Backup)

When AI is enabled on your engine, each reading candidate is reviewed by AI before being saved. The system uses **Claude AI** as the primary analyst, with **ChatGPT** as an automatic backup if Claude is unavailable.

- **Confidence Score** (0-100) - How confident the AI is in the trade
- **Recommendation** - EXECUTE (strong reading), HOLD (wait for better setup), or SKIP (not worth trading)
- **Sizing Adjustment** - AI may suggest increasing or decreasing position size
- **Rationale** - Written analysis explaining the AI's reasoning
- **Key Risks** - 1-3 specific risk factors to watch
- **Model Used** - Shows which AI provided the analysis (Claude or GPT-4o)

### Auto-Execute

When both AI and Auto-Execute are enabled, trades that meet ALL of these criteria are placed automatically:

1. Strategy score >= your confidence threshold (configurable from 25% to 100%)
2. AI confidence >= your confidence threshold
3. AI recommendation = EXECUTE
4. All risk control rules pass (cash reserve, position limits, etc.)

If any criterion isn't met, the reading is saved as "pending" for your manual review. The AI acts as a second opinion - even if the strategy score is high, the AI can block execution by recommending HOLD or SKIP.

---

## Editing an Engine

Click the **Edit** button on any engine card to modify:

- Engine name and scan frequency
- Policy strategy, symbols, and risk appetite
- Custom strategy conditions
- Risk control parameters (including drawdown, trailing stop, and profit protection)
- Claude AI settings

Changes take effect on the next scheduled scan.

## Pausing and Deleting

- **Pause** — Stops the engine from scanning. No new readings are generated but existing trades continue to be monitored. Click again to resume.
- **Delete** — Permanently removes the engine, its EventBridge schedule, and Alpaca credentials. Existing readings and trades are preserved for your records.

---

## Trade Protection

The engine includes three layers of automatic trade protection that work together to limit losses and lock in profits on open trades. All three are enabled by default and can be toggled per engine.

### Max Drawdown Protection

If an open trade's loss exceeds the configured **Max Drawdown %** of the position's entry equity, the trade is immediately closed on Alpaca. This prevents any single trade from causing outsized damage to your account.

- Default: 3% (configurable 1–10%)
- Example: If you entered a $10,000 position, a 3% drawdown limit closes the trade automatically if the loss exceeds $300
- Exit reason shown on closed trade: **max_drawdown**

### Trailing Stop

After a trade shows sustained upward momentum, the engine sets an ATR-based trailing stop that only moves up, never down. This locks in gains as the price rises.

**How it works:**
1. The engine tracks consecutive price gains of >1% between monitoring cycles
2. After **2 consecutive gains >1%**, a trailing stop is calculated using ATR and the trade's high since entry
3. The trailing stop = highest price since entry minus (ATR × volatility multiplier)
4. For bracket orders, the stop leg on Alpaca is automatically updated to the new level
5. If the price falls back to the trailing stop level, the trade is closed

**Volatility-adjusted multiplier** (matches the Stock Research system):
| Daily Volatility | Multiplier | Stop Tightness |
|-----------------|------------|----------------|
| < 2% | 1.5× ATR | Tight (low vol stock) |
| 2–4% | 2.0× ATR | Normal |
| > 4% | 2.5× ATR | Wide (high vol stock) |

Exit reason shown: **trailing_stop**

### Profit Protection

When a trade is profitable, the engine checks for market reversal signals. If 2 or more of the following conditions are detected simultaneously, the trade is closed to protect gains:

1. **Overbought Reversal** — RSI > 75 AND MACD histogram turns negative
2. **Trend Breakdown** — Price falls below the 50-day moving average
3. **Volume Dry-up** — Volume drops below 60% of the 20-day average
4. **AVWAP Negative** — Price falls below the anchored VWAP (institutional support level)

This is the same 2-of-4 reversal warning system used by the Stock Research feature, adapted for algo trades.

Exit reason shown: **profit_protection**

### Wash Sale Protection

When enabled, the engine prevents you from re-entering a position in any symbol that was closed at a loss within the last 30 days. This aligns with the IRS wash sale rule, which disallows claiming a tax loss if you repurchase substantially identical securities within 30 days.

**How it works:**
1. When you close a trade at a loss, the symbol enters a 30-day cooldown period
2. During this period, the engine will **not generate new readings** for that symbol (skipped during scanning)
3. If you try to **manually execute** a pending reading for that symbol, the trade is blocked with an explanation
4. After 30 days from the losing trade's exit date, the symbol is cleared and can be traded again

This protection is **off by default** — enable it in the Trade Protection section when creating or editing your engine. A yellow "Wash Sale Protection" badge appears on the engine card when active.

**Note**: This is a convenience feature to help avoid wash sales — it is not tax advice. Consult a tax professional for your specific situation.

### Scaled Entry (Stepped Tranches)

When enabled, the engine scales into positions gradually instead of entering the full size at once. This reduces risk by requiring price confirmation before committing fully.

**How it works (3-tranche mode):**
1. **Tranche 1 (40%)** — Placed immediately when the reading triggers. This is your starter position.
2. **Tranche 2 (30%)** — Added automatically when price moves **0.5x ATR** in your favor. The move confirms the trade is working.
3. **Tranche 3 (30%)** — Added when price moves **1.0x ATR** in your favor. Full conviction.

**2-tranche mode:** Tranche 1 = 60%, Tranche 2 = 40% (confirms at 0.5x ATR).

**Cancellation:** If price moves **0.5x ATR against** your entry before the next tranche fills, remaining tranches are cancelled. You keep the starter position with its stop loss.

**Example:** You get a BUY reading on AMZN at $200 with ATR = $4.
- Tranche 1: Buy 40 shares at $200 (40% of 100 target)
- Price rises to $202 (+$2 = 0.5x ATR) → Tranche 2: Buy 30 more shares
- Price rises to $204 (+$4 = 1.0x ATR) → Tranche 3: Buy final 30 shares
- If price dropped to $198 (-$2 = 0.5x ATR against) before tranche 2 → Cancel remaining, keep 40 shares

Each tranche is a separate order on Alpaca. All tranches share the same stop loss and target. Enable this in the Risk Controls section when creating or editing your engine.

### Capital Preservation (Daily & Weekly Drawdown Limits)

The engine tracks your daily and weekly realized P&L. If losses exceed your configured limits, the engine automatically pauses to prevent further damage.

**Daily Drawdown** (default: 5% of account equity):
- If your closed trades today have lost more than this % of your account value, the engine pauses until end of trading day (8 PM ET)
- No new readings are generated, but existing bracket order stops on Alpaca still fire normally

**Weekly Drawdown** (default: 10% of account equity):
- If your closed trades this week have lost more than this %, the engine pauses until end of trading week (Friday 8 PM ET)

**After a pause ends:**
- The engine resumes automatically on the next scheduled run
- Position sizes are **reduced by 50%** for the rest of the period as a safety measure
- The engine card shows daily and weekly P&L so you can monitor progress

**Configuring:**
- Set **Max Daily Drawdown %** (1-25%) and **Max Weekly Drawdown %** (1-50%) when creating or editing your engine
- The engine card shows an **AUTO-PAUSED** banner with the reason and resume time when paused

### Duplicate Symbol Prevention

The engine automatically prevents you from holding multiple concurrent positions in the same symbol. If you already have an open or submitted trade for a symbol, any attempt to execute another trade for that same symbol is blocked. This applies to both manual execution and auto-execution.

### Protection in the Audit Log

All protection events are logged in the Audit Log with detailed information including the specific trigger conditions, price levels, and P&L at exit. Check **Algo Audit** to review protection events for your engine.

---

## Understanding the Indicators

| Indicator | What It Tells You |
|-----------|-------------------|
| **RSI** | Overbought (>70) or oversold (<30) momentum. The sweet spot depends on the strategy. |
| **MACD** | Trend direction and momentum. Positive histogram = bullish momentum. |
| **SMA 20/50** | Short vs medium-term trend. SMA20 > SMA50 = uptrend (golden cross). |
| **Bollinger Bands** | Volatility and mean reversion. Position 0% = at lower band, 100% = at upper band. |
| **Volume Ratio** | Current volume vs 20-day average. >1.5x suggests institutional interest. |
| **ATR** | Average daily price range. Used for stop/target calculation — wider ATR = wider stops. |
| **AVWAP** | Anchored VWAP from the most recent swing low. Above AVWAP = institutional support. Distance shown as ±%. |

---

## Frequently Asked Questions

**Q: When does the engine run?**
Only during extended market hours, Monday through Friday (4 AM – 8 PM ET). It will not run on weekends or outside these hours even if scheduled.

**Q: Can I have multiple engines?**
Yes, if your account limit allows it (default is 1). Contact support to increase your engine limit.

**Q: Is paper trading identical to live?**
Paper trading uses Alpaca's simulated environment. It mirrors live market data but fills are simulated. It's the recommended way to test a strategy before going live.

**Q: What happens if the engine finds a reading but I have no buying power?**
The risk controls check your available equity before any trade. If you don't have enough cash (factoring in the cash reserve %), the trade is blocked and the reading is saved as pending with a violation note.

**Q: How do I change which stocks are scanned?**
Edit your engine and update the symbols list in the policy configuration. Changes take effect on the next scan.

**Q: What is the difference between Custom Strategy and condition overrides?**
When you select a built-in strategy (like Momentum) and add conditions, those conditions are merged on top of the strategy's built-in rules. When you select Custom Strategy, your conditions ARE the entire strategy — there are no built-in rules underneath. Use Custom Strategy when you want full control, and overrides when you want to tweak a proven approach.

**Q: What does the Weight field do in custom conditions?**
Weight controls how much influence a condition has on the total score. All weights are normalized — a condition with weight 0.20 has twice the influence of one with weight 0.10. The total score is calculated as: (sum of passed condition weights / sum of all weights) x 100.

**Q: Do fundamental indicators slow down the engine?**
Fundamental data (P/E, ROE, etc.) is only fetched from FMP when your conditions use fundamental indicators. It is cached for 1 hour per symbol. If your strategy only uses technical indicators, no extra API calls are made.

**Q: What is Wash Sale Protection?**
When enabled, it blocks you from re-trading any symbol that was closed at a loss within the last 30 days. This helps you stay compliant with the IRS wash sale rule, which disallows claiming a tax loss deduction if you buy back the same stock within 30 days. The engine skips reading generation for blocked symbols and prevents manual execution too.

**Q: Can I have two open trades in the same stock?**
No. The engine automatically blocks duplicate symbol trades. If you already have an open or submitted position in a symbol, you cannot execute another trade for that same symbol until the first one is closed.

**Q: What happens when my engine gets auto-paused?**
If your daily or weekly losses exceed the configured drawdown limits, the engine automatically pauses. No new readings are generated, but existing bracket order stops on Alpaca continue to fire normally (your open positions are still protected). The engine resumes automatically at the end of the pause period with 50% reduced position sizes as a safety measure. You'll see an AUTO-PAUSED banner on the engine card with the reason and resume time.

**Q: Can I manually resume a paused engine?**
Currently, auto-paused engines resume automatically at the end of the period (end of day or end of week). If you need to resume earlier, you can edit the engine and re-save it, which clears the pause state.
`;

export const tastyTradeConnection = `# TastyTrade Brokerage Connection

The TastyTrade Brokerage Connection lets you link your TastyTrade account to the Wealth Financial Systems so the platform can display your live account data — buying power, net liquidating value, cash balance, and open positions. This feature is available to users with Notches access.

## What It Does

Once connected, you can view your TastyTrade account snapshot directly from your Profile page:

- **Options Buying Power** — Capital available for options trades
- **Equity Buying Power** — Capital available for stock trades
- **Net Liquidating Value** — Total account value if all positions were closed
- **Cash Balance** — Uninvested cash in the account
- **Open Positions** — Number of currently held positions

This data is fetched live from TastyTrade each time you click **Refresh Account Data**. It is read-only — the platform never places trades or modifies your account.

## How to Set Up

### Step 1 — Create an OAuth App in TastyTrade

1. Log in to your TastyTrade account at **tastytrade.com**
2. Click **Manage** in the top navigation, then select **Profile**
3. Click the **API** tab
4. Click **OAuth Application** to open the OAuth apps section
5. Click **Create Application** — give it a name like **Wealth Financial Systems**
6. TastyTrade will display a **Client Secret** — **copy and save it now** (you won't see it again)
7. Back on the OAuth Application list, click the **three dots (...)** next to your app's Client ID
8. Click **Create Grant**
9. Select the scopes **Read** and **Trade** (do NOT select OpenAI)
10. Click **Create** — TastyTrade will generate a **Refresh Token** — copy it

You now have two values: the **Client Secret** (from step 6) and the **Refresh Token** (from step 10).

### Step 2 — Connect in the Wealth Financial Systems

1. Log in to the Wealth Financial Systems
2. Go to **Account Settings** in the sidebar
3. Scroll down to the **TastyTrade API Connection** section
4. Paste your **Refresh Token** and **Client Secret** into the fields
5. Click **Connect & Test**
6. If your TastyTrade login has **multiple accounts**, the system will verify your credentials and then show a list of your accounts — select the one you want to use and click **Connect Selected Account**
7. If you only have one account, it connects automatically
8. You'll see a success message with your masked account number (e.g., ****1234)

### Step 3 — View Your Account Data

Once connected, the Profile page shows your account number and a **Refresh Account Data** button. Click it to pull your latest balances and position count.

Your account data also appears automatically on the **Index Options Research** page — the summary bar at the top shows your Options Buying Power, Net Liquidating Value, Cash Balance, and Open Positions alongside the reading data.

## Security & Privacy

- **Encrypted storage** — Your credentials are stored in AWS Secrets Manager, encrypted at rest. They are never stored in plain text or in the database.
- **Read-only access** — The platform only reads your account balances and positions. It cannot and does not place trades, transfer funds, or modify your TastyTrade account in any way.
- **No credential exposure** — After you connect, your refresh token and client secret are never displayed again — not in the UI, not in API responses, not in logs.
- **Instant disconnect** — Click **Disconnect** at any time to permanently delete your stored credentials. The deletion is immediate and irreversible (no recovery period).

## Switching Accounts

If you want to switch to a different TastyTrade account, disconnect first and then reconnect — the account picker will appear again if you have multiple accounts.

## Disconnecting

1. Go to **Account Settings** in the sidebar
2. In the **TastyTrade API Connection** section, click **Disconnect**
3. Your credentials are permanently deleted from Secrets Manager
4. Your account data is no longer accessible from the platform
5. You can reconnect at any time by entering new credentials

## Troubleshooting

**"Invalid credentials. Please check your refresh token and client secret."**
- Double-check that you copied the full refresh token and client secret from TastyTrade
- Make sure you're using the credentials from the correct TastyTrade account
- Refresh tokens can expire — if it's been a while, generate new credentials in TastyTrade

**"Credentials expired, please reconnect"**
- Your TastyTrade refresh token has expired or been revoked
- Go to TastyTrade > API Settings and generate new credentials
- Click **Disconnect** in the Wealth Financial Systems, then reconnect with the new credentials

**"Could not retrieve account information"**
- Your credentials authenticated successfully but the account lookup failed
- This may happen if your TastyTrade account is in a restricted state
- Contact TastyTrade support if the issue persists

**Section not visible on Profile page**
- The TastyTrade Connection section only appears for users with Notches access enabled
- Contact support if you believe you should have access

## Frequently Asked Questions

**Q: Does this place trades on my behalf?**
No. This is strictly read-only. The platform fetches your account balances and position count — nothing more.

**Q: How often is my account data updated?**
Account data is fetched on-demand each time you click **Refresh Account Data**. It is not polled automatically.

**Q: Can I connect multiple TastyTrade accounts?**
One account at a time per user. If your TastyTrade login has multiple accounts (e.g., Individual and IRA), the system shows an account picker during setup so you can choose which one to connect. To switch accounts, disconnect and reconnect.

**Q: Where does my account data appear?**
In two places: on the **Account Settings** page under the TastyTrade section (with a Refresh button), and automatically on the **Index Options Research** page in the summary bar at the top (Options BP, Net Liq, Cash, Open Positions).

**Q: What happens to my data if I disconnect?**
Your credentials are permanently deleted from our systems. No account data is stored — it's always fetched live. After disconnecting, there is no residual data.

**Q: Is my TastyTrade password stored?**
No. The connection uses OAuth refresh tokens, not your password. The platform never sees or stores your TastyTrade login credentials.
`;

export const notches = `# Notches

Notches is the Wealth Financial Systems options-income research product. Each cron run scans the live options chain for SPX, SPY, ES, and MES; scores every eligible short-put candidate against a five-component model; cross-checks the top picks with Claude and ChatGPT; and persists everything as a timestamped snapshot you can review on the live page or via a daily email digest.

This guide explains what the page shows, what the columns mean, how the model works, and how to set your preferences.

---

## The trade idea

Every Notches pick is a **short cash-secured (or naked) put** — you sell a put option on the underlying and collect the premium ("credit") upfront. If the underlying stays above your strike at expiration, the option expires worthless and you keep the full credit. If it drops below, you're obligated to buy the underlying at the strike (or, for futures, close at a loss).

The model identifies puts that maximize credit-per-day relative to risk, weighted by your selected preset.

---

## Risk presets

You choose one of three presets in your profile — it controls how Notches ranks candidates and which one becomes "the WFS pick" at each DTE.

- **Conservative** — emphasizes cushion (distance from spot) and theta efficiency. Default for new accounts. Smaller credits, deeper out-of-the-money, higher probability of profit.
- **Balanced** — even weighting across credit-velocity, cushion, theta, vega exposure, and liquidity.
- **Aggressive** — emphasizes credit-per-day and theta-capture. Larger credits, closer to the money, lower probability of profit.

Universe cap is \\\`|Δ| ≤ 0.35\\\` across all presets — no Notches pick will have a delta steeper than 0.35 regardless of preset. Within that universe, each preset selects differently. Switch presets any time on your profile page — the same snapshot data is re-ranked instantly.

---

## The reading table

Each row on the Notches page is one trade idea at one DTE. Columns (sortable by clicking the header — hover any header for a definition):

| Column | What it means |
|---|---|
| **DTE** | Days to expiration — calendar days from today (ET) to the option's expiration date. Computed live, so it's always current. |
| **Strike** | Put strike price (the level the underlying must stay above to keep the full credit). |
| **Δ** | Option delta — approximate probability the put finishes in-the-money. Lower \\\`|Δ|\\\` = further out-of-the-money = safer for sellers. |
| **Credit** | Premium you collect upfront when selling the put. Maximum profit if it expires worthless. |
| **Win %** | Probability of profit — odds the put expires worthless. Derived from delta + days to expiration. |
| **Cushion** | Safety buffer in points — how far the underlying can drop from spot before your strike is breached. Bigger cushion = more room to be wrong. |
| **R/R** | Risk-adjusted return (Sharpe-style ratio — annualized return per unit of volatility). Higher = better. *Note: this is a Sharpe-style measure, not literal max-profit / max-loss.* |
| **IV** | Implied volatility — the market's expectation of annualized price movement. Higher IV = richer premium, also more uncertainty. |
| **Score** | The WFS composite score under your selected preset. Higher = better fit for that preset. |
| **Target** | Take-profit buy-to-close price. Closing here locks in the preset's profit target (Conservative 50%, Balanced 60%, Aggressive 70% of the credit). |
| **Stop** | Stop-loss buy-to-close price. Multiplier of original credit (Conservative 1.5×, Balanced 2.0×, Aggressive 2.5×). |
| **Exp** | Expiration date (short format; hover for the full date). |
| **Source** | Which system's pick is in this row's columns — **WFS** (our quantitative model, default), **Claude**, or **ChatGPT**. Smaller outlined chips next to the primary show the other sources that also picked at this DTE, with **✓** (same strike) or **~** (different strike). |
| **Conv** | Convergence Score — see below. |

Rows where the WFS model picked are highlighted in amber. Rows where only an AI picked (no WFS candidate at that DTE) get a blue (Claude) or green (ChatGPT) accent.

Click any row to expand it — you'll see each source's reasoning (the "Why this pick" panel), exit framework details, and alternate WFS candidates at that DTE.

---

## Convergence Score

Three independent systems evaluate every DTE: our quantitative WFS model, Claude, and ChatGPT. The AIs never see each other's output or the model's score. The Convergence Score reflects how strongly they agree.

- **STRONG** — All three picked the same strike. The highest-confidence reading Notches produces. When three independent systems converge, the trade is more likely robust.
- **MEDIUM** — Two of three agree. Hover the badge to see which two — "WFS + one AI" (quant and one qualitative align) or "Both AIs agree" (two qualitative systems converged).
- **MIXED** — Each picks a different strike. The market is ambiguous at this DTE. If you trade, consider sizing smaller or selecting based on the individual trade metrics.
- **SOLO** — Only one system picked at this DTE. There's nothing to compare against, so convergence isn't measurable. Treat the pick on its own merits.

Convergence measures agreement under uncertainty — not profit prediction. It tells you how confident the system is in its selection, not how much the trade will make.

---

## Snapshots

Multiple snapshots are taken each trading day — pre-market, mid-day, end-of-day, and after-hours. The Notches page shows the latest snapshot by default; use the snapshot selector at the top of the page to view earlier ones from the same day (snapshots are sorted newest-first).

Each snapshot is independent — the model re-scores the entire candidate universe from the freshly-pulled options chain. The columns shown are always relative to that snapshot's data.

---

## Daily email digest

If you've enabled **Notches Daily Email Digest** in your profile, you'll receive one branded email per subscribed symbol (SPX / SPY / ES / MES) at the end of every trading day. The email shows:

- Spot price, VIX, skew regime, and macro context at the latest snapshot.
- Top 3 picks under each of the three presets — your preset surfaces first.
- A Convergence Score on every pick.
- A button to open the live page for deeper inspection.

The digest never replaces the live page — it's a daily summary so you don't have to log in to see what the model picked. You can disable it any time in **Profile → Subscription Preferences**.

---

## SMS alerts (optional)

If you've enabled **Notches Alerts** in your SMS preferences (requires a verified phone + paid tier), you'll receive one SMS per producer run per subscribed symbol with the top WFS pick under your selected preset. SMS includes the strike, expiration, credit, probability of profit, target, and stop, plus a link to the live page.

SMS preferences are separate from email — you can have either, both, or neither.

---

## Setting your preferences

On your **Profile** page:

1. **Your risk preference** (radio: Conservative / Balanced / Aggressive) — controls preset ranking everywhere.
2. **Notches Daily Email Digest** (under Subscription Preferences) — opt-in for the daily email.
3. **Notches Alerts** (under SMS Notifications) — opt-in for per-snapshot SMS (requires verified phone + paid tier).

Preset changes apply immediately — the live page re-ranks on the next refresh. Email/SMS toggles take effect on the next cron run.

---

## What Notches does NOT do

- Place trades for you. Notches is an analysis tool; every trade is executed in your own brokerage account.
- Predict outcomes. The model identifies high-probability theta-capture opportunities under your preset; it doesn't guarantee any specific trade will be profitable.
- Replace your judgment. The Convergence Score, AI reasoning, and detailed metrics are there to inform — not decide. The systems can all agree on a pick that still loses money if the market moves sharply against the position.

---

## Risk disclosure

Trading options involves substantial risk and can result in losses greater than the original premium collected. Past performance is not indicative of future results. Notches is provided for educational and informational purposes only and does not constitute investment, financial, or trading advice. You are responsible for your own trading decisions. Wealth Financial Systems LLC is not a registered investment advisor.
`;

export const allDocsContent = [
  gettingStarted,
  dashboard,
  notches,
  optionsSignals,
  stockSignals,
  howWePickTrades,
  ivReports,
  stockDailyReport,
  algoTrading,
  netWorthTracker,
  fireCalculator,
  lifecyclePlanner,
  bucketVisualizer,
  playbook,
  subscriptionsBilling,
  signalSchedule,
  tastyTradeConnection,
].join('\n\n---\n\n');
